import api from "./api";
import { UserInterface } from "../interfaces/UserInterface";
import { FileDocument } from "./shipmentServices";
import { base64ToFile } from "../utils";
import {
  BusinessUnitInterface,
  Nullable,
  PODInsertAttachmentResponse,
  PODInsertResponse,
  ResponseInterface,
  SavePODv2Interface,
} from "../interfaces";

const baseURLAPI = process.env.REACT_APP_API_HOST;

// DEPRECATED: Use savePODv2 instead.
export const savePOD = async (
  shipmentId: string,
  accountID: string,
  deliveryPersonName: string,
  deliveryIdentification: string,
  observation: string,
  user: UserInterface,
  businessUnit: BusinessUnitInterface
): Promise<ResponseInterface<PODInsertResponse>> => {
  const uri = `${baseURLAPI}/POD`;
  const body = JSON.stringify({
    BUCodeDelivery: businessUnit.code,
    DeliveryAuthorization: false,
    DeliveryDate: new Date().toISOString(),
    AccountID: accountID,
    ShipmentID: shipmentId,
    DeliveryPersonName: deliveryPersonName,
    DeliveryIdentification: deliveryIdentification,
    DeliveryObservation: observation,
    CreationUser: user.userLogin,
    CreationDate: new Date().toISOString(),
  });
  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const savePODv2 = async (request: SavePODv2Interface): Promise<ResponseInterface<PODInsertResponse>> => {
  const uri = `${baseURLAPI}/POD/InsertV2`;
  const formData = new FormData();
  formData.append("BUCodeDelivery", request.businessUnit.code);
  formData.append("DeliveryAuthorization", "false");
  formData.append("DeliveryDate", new Date().toISOString());
  formData.append("AccountID", request.accountID);
  formData.append("ShipmentID", request.shipmentId);
  formData.append("DeliveryPersonName", request.deliveryPersonName);
  formData.append("DeliveryIdentification", request.deliveryIdentification);
  formData.append("DeliveryObservation", request.observation);
  formData.append("ShipmentNumber", request.shipmentNumber);
  formData.append("PhysicalPODConfirmed", request.isPhysicalSignature ? "true" : "false");
  formData.append("CreationUser", request.user.userLogin);
  formData.append("CreationDate", new Date().toISOString());

  // if the fingerprint is not null or empty, append it to the formData
  if (request.fingerprint && request.fingerprint !== "") {
    const fingerprintPNG = base64ToFile(request.fingerprint, "image/png", "fingerprint.png");
    const fingerprintBlob = new Blob([fingerprintPNG], { type: "image/png" });
    formData.append("Fingerprint", fingerprintBlob, "fingerprint-POD.png");
  }

  // if the photoAttachments is not null or empty, append it to the formData
  if (request.photoAttachments && request.photoAttachments.length > 0) {
    for (let i = 0; i < request.photoAttachments.length; i++) {
      const attachmentPNG = base64ToFile(
        request.photoAttachments[i],
        "image/png",
        "attachment-POD.png"
      );
      formData.append(`PhotoAttachment${[i + 1]}`, attachmentPNG);
    }
  }

  try {
    const response = await api.post(uri, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const updatePOD = async (
  shipmentList: string[],
  fingerprintAttch: string | null,
  physicalSignature: boolean | null,
  updateUser: string,
  statusID: number = 1
): Promise<ResponseInterface<boolean>> => {
  const uri = `${baseURLAPI}/POD/Update`;
  const body = JSON.stringify({
    ShipmentNumber: shipmentList,
    ConsigneeFingerPrintAttch: fingerprintAttch,
    PhysicalPODConfirmed: physicalSignature,
    StatusID: statusID,
    UpdateUser: updateUser,
  });
  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const getPODDocument = async (
  shipmentNumbers: string[]
): Promise<Nullable<FileDocument>> => {
  const uri = `${baseURLAPI}/v1/Shipment/DownloadDocumentDelivery`;

  const body = JSON.stringify({
    List: shipmentNumbers.map((shipmentNumber) => ({ shipmentNumber })),
    type: 0,
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      responseType: "blob",
    });

    if (response.status === 200) {
      return {
        file: response.data,
        fileName: `PODDocument_${shipmentNumbers[0]}.pdf`,
      };
    }
  } catch (error) { }
  return null;
};

export const savePODAttachment = async (
  fingerprint: string | undefined,
  photoAttachments: string[] | undefined,
  podID: string,
  creationUser: string,
  statusID: number = 1
): Promise<ResponseInterface<PODInsertAttachmentResponse>> => {
  const uri = `${baseURLAPI}/POD/InsertAttachment`;
  const formData = new FormData();
  if (fingerprint && fingerprint !== "") {
    const fingerprintPNG = base64ToFile(fingerprint, "image/png", "fingerprint.png");
    const fingerprintBlob = new Blob([fingerprintPNG], { type: "image/png" });
    formData.append("Fingerprint", fingerprintBlob, "fingerprint-POD.png");
  }
  if (photoAttachments && photoAttachments.length > 0) {
    for (let i = 0; i < photoAttachments.length; i++) {
      const attachmentPNG = base64ToFile(
        photoAttachments[i],
        "image/png",
        "attachment-POD.png"
      )
      formData.append(`PhotoAttachment${[i + 1]}`, attachmentPNG);
    }
  }

  formData.append("PODID", podID);
  formData.append("CreationUser", creationUser);
  formData.append("StatusID", statusID.toString());

  try {
    const response = await api.post(uri, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};
